import React, { useState, useEffect, useRef, useContext } from 'react';
import { TutorialContext } from './TutorialManager';

// Separate Desktop Component
const DesktopBunny = ({ handleOptionClick, showBubble, bubbleMessage, isMenuOpen, handleBunnyClick, menuRef, bunnyRef }) => (
  <div className="fixed bottom-0 left-0">
    {/* Speech Bubble */}
    {showBubble && (
      <div className="fixed bottom-40 left-14 z-40">
        <div className="bg-white rounded-xl p-2 shadow-md text-sm text-gray-700 relative max-w-[160px] whitespace-pre-wrap">
          {bubbleMessage}
          <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-3 h-3 bg-white"/>
        </div>
      </div>
    )}
    
    {/* Menu Options */}
    {isMenuOpen && (
      <div 
        ref={menuRef} 
        className="fixed bottom-40 left-8 z-40"
      >
        <div className="bg-white rounded-lg shadow-lg overflow-hidden min-w-[160px]">
          <button
            onClick={() => handleOptionClick('tutorial')}
            className="w-full px-4 py-2.5 text-left hover:bg-gray-50 text-gray-700 font-medium"
          >
            Watch Tutorial
          </button>
          <button
            onClick={() => handleOptionClick('support')}
            className="w-full px-4 py-2.5 text-left hover:bg-gray-50 text-gray-700 font-medium"
          >
            Support Us
          </button>
        </div>
      </div>
    )}
    
    {/* Bunny Button */}
    <div 
      ref={bunnyRef} 
      className="fixed bottom-24 left-12 z-40"
    >
      <button
        onClick={handleBunnyClick}
        className="w-32 h-24 transition-transform hover:-translate-y-2 duration-300 relative"
      >
        <img
          src="/bunny0.png"
          alt="Helper Bunny"
          className="w-full h-full object-contain"
          style={{
            transform: 'translateY(40%)',
            filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))'
          }}
          onError={(e) => {
            console.error('Error loading bunny image:', e);
            console.log('Attempted image path:', e.target.src);
          }}
        />
      </button>
    </div>
  </div>
);

// Restore our previous successful mobile settings
const MobileBunny = ({ handleOptionClick, showBubble, bubbleMessage, isMenuOpen, handleBunnyClick, menuRef, bunnyRef }) => (
  <div className="fixed bottom-0 left-0">
    {/* Menu Options */}
    {isMenuOpen && (
      <div 
        ref={menuRef}
        className="fixed bottom-32 left-4 z-40"
      >
        <div className="bg-white rounded-lg shadow-lg overflow-hidden min-w-[5px]">
          <button
            onClick={() => handleOptionClick('tutorial')}
            className="w-full px-3 py-3 text-left hover:bg-gray-50 text-gray-700 font-medium"
          >
            Tutorial
          </button>
          <button
            onClick={() => handleOptionClick('support')}
            className="w-full px-3 py-3 text-left hover:bg-gray-50 text-gray-700 font-medium"
          >
            Support
          </button>
        </div>
      </div>
    )}

    {/* Speech Bubble */}
    {showBubble && (
      <div className="fixed bottom-28 left-6 z-40">
        <div className="bg-white rounded-xl p-2 shadow-md text-sm text-gray-700 relative max-w-[120px] whitespace-pre-wrap">
          {bubbleMessage}
          <div className="absolute bottom-0 left-2 transform translate-y-1/2 rotate-45 w-3 h-3 bg-white"/>
        </div>
      </div>
    )}

    {/* Bunny Button */}
    <div ref={bunnyRef} className="fixed bottom-20 left-4 z-40">
      <button
        onClick={handleBunnyClick}
        className="w-20 h-16 transition-transform hover:-translate-y-2 duration-300 relative"
      >
        <img
          src="/bunny0.png"
          alt="Helper Bunny"
          className="w-full h-full object-contain"
          style={{
            transform: 'translateY(40%)',
            filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))'
          }}
        />
      </button>
    </div>
  </div>
);

const BunnyHelper = () => {
  const { showTutorialAgain } = useContext(TutorialContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [bubbleMessage, setBubbleMessage] = useState('');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const menuRef = useRef(null);
  const bunnyRef = useRef(null);

  const messages = [
    "Tap me!",
    "Don't want to go back to talking to your houseplants? Then Support Us!"
  ];

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show/hide bubble periodically
  useEffect(() => {
    const showBubbleRandomly = () => {
      if (!isMenuOpen) {
        setShowBubble(true);
        setBubbleMessage(messages[Math.floor(Math.random() * messages.length)]);
        
        // Hide bubble after 5 seconds
        setTimeout(() => {
          setShowBubble(false);
        }, 5000);
      }
    };

    // Show bubble every 15 seconds
    const interval = setInterval(showBubbleRandomly, 15000);
    return () => clearInterval(interval);
  }, [isMenuOpen]);

  // Handle click outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        bunnyRef.current &&
        !bunnyRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef, bunnyRef]);

  const handleBunnyClick = () => {
    setShowBubble(false);
    setIsMenuOpen(true);
  };

  const handleOptionClick = (option) => {
    if (option === 'tutorial') {
      showTutorialAgain();
    } else if (option === 'support') {
      window.open('https://www.buymeacoffee.com/talkconnect', '_blank');
    }
    
    // Delay menu close
    setTimeout(() => {
      setIsMenuOpen(false);
    }, 200);
  };

  const props = {
    handleOptionClick,
    showBubble,
    bubbleMessage,
    isMenuOpen,
    handleBunnyClick,
    menuRef,
    bunnyRef
  };

  return isDesktop ? <DesktopBunny {...props} /> : <MobileBunny {...props} />;
};

export default BunnyHelper;